import { useState, useEffect } from 'react';
import { useSettings } from '../../../contexts/SettingsContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { extractYouTubeVideoId, getYouTubeThumbnail, getYouTubeWatchUrl } from '../../../utils/youtube';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import VideoLightbox from '../../../components/ui/VideoLightbox';
import type { HeroContent } from '../types';

interface HeroSectionProps {
  content: HeroContent;
  onRegisterClick: () => void;
  onLoginClick: () => void;
}

const HeroSection = ({ content, onRegisterClick, onLoginClick }: HeroSectionProps) => {
  const { heroYouTubeUrl, heroVideoThumbnail, systemLogo, systemLogoDark, platformName } = useSettings();
  const { effectiveTheme } = useTheme();
  const { t, isLoading: translationsLoading } = useTranslation();
  
  const getTranslation = (key: string, fallback: string): string => {
    if (translationsLoading) return fallback;
    const translation = t(key);
    return translation === key ? fallback : translation;
  };
  const [videoId, setVideoId] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  // Use a data URI for placeholder to avoid 404 errors
  const defaultImageUrl = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600'%3E%3Crect fill='%23e5e7eb' width='800' height='600'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EImage Coming Soon%3C/text%3E%3C/svg%3E";

  useEffect(() => {
    if (heroYouTubeUrl) {
      const id = extractYouTubeVideoId(heroYouTubeUrl);
      if (id) {
        setVideoId(id);
        // Use custom thumbnail if provided and not empty, otherwise use YouTube thumbnail
        setThumbnailUrl((heroVideoThumbnail && heroVideoThumbnail.trim() !== '') ? heroVideoThumbnail : getYouTubeThumbnail(id));
        setVideoUrl(getYouTubeWatchUrl(id));
      }
    } else {
      setVideoId(null);
      setThumbnailUrl(null);
      setVideoUrl(null);
    }
  }, [heroYouTubeUrl, heroVideoThumbnail]);

  const handleVideoClick = () => {
    if (videoId) {
      setIsLightboxOpen(true);
    }
  };

  return (
    <section className="relative min-h-[600px] lg:min-h-[700px] flex items-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <div className="container mx-auto px-4 lg:px-8 py-16 lg:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 animate-fade-in">
            {/* Logo */}
            {(systemLogo || systemLogoDark) && (
              <div className="mb-4">
                <Image
                  src={effectiveTheme === 'dark' && systemLogoDark ? systemLogoDark : (systemLogo || '')}
                  alt={platformName || 'Sisi ni Tanzania'}
                  className="w-auto h-auto max-w-none object-contain"
                />
              </div>
            )}
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
              <Icon name="Sparkles" size={18} className="text-primary" />
              <span className="text-sm font-medium text-primary">{getTranslation('landing.hero.badge', 'Empowering Youth Voices')}</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold text-foreground leading-tight">
              {content.headline}
            </h1>

            <p className="text-xl lg:text-2xl text-muted-foreground font-medium">
              {content.subheadline}
            </p>

            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
              {content.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="UserPlus"
                iconPosition="left"
                onClick={onRegisterClick}
                className="w-full sm:w-auto">

                {content.primaryCTA}
              </Button>

              <Button
                variant="outline"
                size="lg"
                iconName="LogIn"
                iconPosition="left"
                onClick={onLoginClick}
                className="w-full sm:w-auto">

                {content.secondaryCTA}
              </Button>
            </div>

            <div className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) =>
                <div
                  key={i}
                  className="w-10 h-10 rounded-full border-2 border-background bg-primary/20 flex items-center justify-center">

                    <Icon name="User" size={18} className="text-primary" />
                  </div>
                )}
              </div>
              <div className="text-sm">
                <p className="font-semibold text-foreground">{getTranslation('landing.hero.activeYouth', '10,000+ Active Youth')}</p>
                <p className="text-muted-foreground">{getTranslation('landing.hero.joinCommunity', 'Join the community')}</p>
              </div>
            </div>
          </div>

          <div className="relative animate-slide-in">
            <div className="relative aspect-square lg:aspect-[4/3] rounded-2xl overflow-hidden elevation-lg">
              <Image
                src={thumbnailUrl || defaultImageUrl}
                alt="Diverse group of young African professionals collaborating on laptops in modern bright office space with plants"
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
              <button
                onClick={handleVideoClick}
                className="absolute inset-0 flex items-center justify-center group"
                aria-label={videoUrl ? "Watch video on YouTube" : "Play video"}
              >
                <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center group-hover:scale-110 transition-smooth elevation-lg">
                  <Icon name="Play" size={32} className="text-primary-foreground ml-1" />
                </div>
              </button>
            </div>

            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent rounded-full blur-3xl opacity-30" />
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-secondary rounded-full blur-3xl opacity-30" />
          </div>
        </div>
      </div>

      {/* Video Lightbox */}
      <VideoLightbox
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        videoId={videoId}
        videoUrl={videoUrl}
      />
    </section>
  );
};

export default HeroSection;